import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_Populate, T_Replace } from "types/_utils.types";
import { BlogDto, BlogSchemaDto, BlogSitesDto } from "dtos/blog.dto";
import { UserSchemaDto } from "dtos/user.dto";
import { T_Api_Paginated } from "./api.types";

export type PopulatedBlog = T_Populate<BlogSchemaDto, { author: Pick<UserSchemaDto, "_id" | "name" | "email"> }>;

export const blogService = {
  useGetAllPaginated: ({
    page,
    limit,
    categories = [],
    status = [],
    visibility = [],
    searchString = "",
    sortOrder = -1,
  }: {
    page: number;
    limit: number;
    categories?: string[];
    status?: string[];
    visibility?: string[];
    searchString: string;
    sortOrder?: number;
  }) => {
    return useQuery({
      queryKey: ["blog/all", page, limit, categories, status, visibility, sortOrder],
      queryFn: async () => {
        const res = await api.get(
          `/blog?page=${page}&limit=${limit}&status=${JSON.stringify(status)}&visibility=${JSON.stringify(visibility)}&categories=${JSON.stringify(categories)}&queryString=${searchString}&sortOrder=${sortOrder}`,
        );
        return res.data?.result as T_Api_Paginated<PopulatedBlog>;
      },
      placeholderData: (prev) => prev,
    });
  },

  useSearchBlogs: ({ page, limit, term }: { page: number; limit: number; term?: string }) => {
    return useQuery({
      queryKey: ["blog/all", page, limit],
      queryFn: async () => {
        const res = await api.get(`/blog/search?queryString=${term}&page=${page}&limit=${limit}`);
        return res.data?.result as T_Api_Paginated<PopulatedBlog>;
      },
    });
  },

  useGetByIdOrSlug: (id: string | undefined) => {
    return useQuery({
      queryKey: ["blog", id],
      queryFn: async () => {
        const res = await api.get(`/blog/${id}`);
        return res.data?.result as PopulatedBlog;
      },
      enabled: !!id,
    });
  },

  useCreateBlog: () => {
    return useMutation({
      mutationKey: ["blog", "create"],
      mutationFn: async (body: BlogDto) => {
        const res = await api.post("/blog", body);
        return res.data?.result as BlogDto;
      },
    });
  },
  useUpdateByIdOrSlug: () => {
    return useMutation({
      mutationKey: ["blog/update"],
      mutationFn: async ({ id, body }: { id: string; body: BlogDto }) => {
        return api.patch(`/blog/${id}`, body);
      },
    });
  },
  useDeleteBySlugOrId: () => {
    return useMutation({
      mutationKey: ["blog/delete"],
      mutationFn: async (id: string) => {
        return api.delete(`/blog/${id}`);
      },
    });
  },
  useGetBlogByCategory: (queries: string[]) => {
    return useQuery({
      queryKey: ["blog/category"],
      queryFn: async () => {
        const res = await api.get(`/blog/?categories=${queries}`);
        return res.data?.result as T_Api_Paginated<BlogDto>;
      },
    });
  },
  useUpdateVisibility: () => {
    return useMutation({
      mutationKey: ["update/visibility"],
      mutationFn: ({ id, visiblity }: { id: string; visiblity: string }) => {
        return api.put(`/blog/visibility/${id}`, { visiblity });
      },
    });
  },
  useUpdateStatus: () => {
    return useMutation({
      mutationKey: ["update/status"],
      mutationFn: ({ id, status }: { id: string; status: string }) => {
        return api.patch(`/blog/status/${id}`, { status });
      },
    });
  },
  useUpdateFeature: () => {
    return useMutation({
      mutationKey: ["update/feature"],
      mutationFn: ({ id, feature }: { id: string; feature: boolean }) => {
        return api.patch(`/blog/featured/${id}`, { featured: feature });
      },
    });
  },
  useGetBlogSites: () => {
    return useQuery({
      queryKey: ["/blog-sites"],
      queryFn: async () => {
        const res = await api.get(`/blog-sites`);
        return res.data?.result as BlogSitesDto[];
      },
    });
  },

  // useGetAllMessageByBlogId,
  // useSendMessage,
};
